// lib/firebase/auth.ts
import {
	createUserWithEmailAndPassword,
	signOut as firebaseSignOut,
	onAuthStateChanged,
	signInWithEmailAndPassword,
	signInWithPopup,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { type User, usersService } from "../api";
import { auth, db, googleProvider } from "./client";

export class AuthService {
	// Sign up with email and password
	async signUp(email: string, password: string, name: string): Promise<User> {
		let finalUser;
		try {
			// First create user with Firebase Auth
			const userCredential = await createUserWithEmailAndPassword(
				auth,
				email,
				password,
			);
			const firebaseUser = userCredential.user;

			// Create user document in backend using email as the link
			const userRequest = {
				name: name || email.split("@")[0],
				email: email, // Email is the linking field
				photoURL: firebaseUser.photoURL || undefined,
			};

			// Backend will generate UUID and create user
			const createdUser = await usersService.createUser({
				createUserRequest: userRequest,
			});
			finalUser = createdUser;
		} catch (error) {
			console.error("Error during sign up:", error);
			throw error;
		}
		await new Promise((resolve) => setTimeout(resolve, 500));
		return finalUser;
	}

	async signIn(email: string, password: string): Promise<User> {
		try {
			// Authenticate with Firebase
			const userCredential = await signInWithEmailAndPassword(
				auth,
				email,
				password,
			);
			const firebaseUser = userCredential.user;

			// Get user from backend using email
			const user = await usersService.getUserByEmail({ email });

			if (!user) {
				throw new Error("User authenticated but not found in backend");
			}
			await new Promise((resolve) => setTimeout(resolve, 500));
			return user;
		} catch (error) {
			console.error("Error during sign in:", error);
			throw error;
		}
	}
	// Sign in with Google
	async signInWithGoogle(): Promise<User> {
		try {
			console.log("Starting Google sign in process");
			const result = await signInWithPopup(auth, googleProvider);
			const firebaseUser = result.user;
			const email = firebaseUser.email;

			if (!email) {
				throw new Error("Google account must have an email address");
			}

			try {
				try {
					const user = await usersService.getUserByEmail({ email });
					console.log("User document already exists in backend");
					await new Promise((resolve) => setTimeout(resolve, 500));
					return user;
				} catch (error) {
					// If error is 404, user doesn't exist - create a new one
					if (error.response && error.response.status === 404) {
						console.log(
							"User not found, creating new user document in backend",
						);
						const newUserRequest = {
							email: email,
							name: firebaseUser.displayName || email.split("@")[0] || "User",
							photoURL: firebaseUser.photoURL || undefined,
						};

						const createdUser = await usersService.createUser({
							createUserRequest: newUserRequest,
						});
						console.log("User document created successfully");
						await new Promise((resolve) => setTimeout(resolve, 500));
						return createdUser;
					} else {
						// For other errors, throw them up
						throw error;
					}
				}
			} catch (error) {
				console.error("Error checking/creating user in backend:", error);
				throw error;
			}
		} catch (error) {
			console.error("Error during Google sign in:", error);
			throw error;
		}
	}

	// Sign out
	async signOut(): Promise<void> {
		try {
			//localStorage.removeItem("organization-storage");
			localStorage.removeItem("user");
			await firebaseSignOut(auth);
			window.location.reload();
		} catch (error) {
			console.error("Error during sign out:", error);
			throw error;
		}
	}

	// Get current user
	getCurrentUser(): FirebaseUser | null {
		return auth.currentUser;
	}

	// Subscribe to auth state changes
	onAuthStateChanged(
		callback: (user: FirebaseUser | null) => void,
	): () => void {
		return onAuthStateChanged(auth, callback);
	}

	// Get user data from Firestore
	async getUserData(userId: string): Promise<User | null> {
		try {
			const userDoc = await getDoc(doc(db, "users", userId));
			if (userDoc.exists()) {
				return userDoc.data() as User;
			}
			return null;
		} catch (error) {
			console.error("Error getting user data:", error);
			throw error;
		}
	}
}

// Create and export a singleton instance
export const authService = new AuthService();
